import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import {
  BarChart3,
  ChevronDown,
  ChevronRight,
  Clock,
  DollarSign,
  HelpCircle,
  TrendingUp,
  ArrowDownCircle,
  Users,
  Wallet,
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import NewtonsCradleLoader from '../components/common/NewtonsCradleLoader';
import { WHATSAPP_URL } from '../components/common/WhatsAppChatButton';
import apiService, {
  extractCommissionHistoryList,
  extractCommissionHistoryPagination,
} from '../utils/apiService';
import { parseEarningDashboard } from '../utils/earningDashboard';
import { getThemeColor } from '../config/appConfig';
import { useFetchOnceByKey } from '../hooks/useMountOnce';

const THEME = getThemeColor('primary');
const SUB_PARTNER_COMMISSIONS_TAB = 'Sub Partner Commissions';
const SOURCE_TABS = ['All', 'Direct Clients', 'Sub Partners', SUB_PARTNER_COMMISSIONS_TAB, 'Campaign Bonus', 'Other Bonus'];
const PAGE_SIZE_OPTIONS = [
  { value: '10', label: '10' },
  { value: '50', label: '50' },
  { value: '100', label: '100' },
];

const toAmount = (val) => Number(val) || 0;

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(toAmount(amount));

const formatCurrency4 = (amount) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  }).format(toAmount(amount));

const formatWidgetCurrency = (amount) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 4,
  }).format(toAmount(amount));

const formatOpenTime = (date) =>
  date
    ? new Date(date).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

const formatPrice = (value) => {
  if (value == null || value === '') return '—';
  const n = Number(value);
  return Number.isNaN(n) ? '—' : n.toFixed(2);
};

const resolveApiPageSize = (size) => Number(size) || 10;

const formatShortDate = (date) => {
  if (!date) return '';
  const d = new Date(date);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const mapSubPartnerRow = (item, index) => {
  const isActiveRaw = item?.isActive ?? item?.IsActive;
  return {
    id: item?.affiliateUserId ?? item?.AffiliateUserId ?? index,
    fullName: item?.fullName ?? item?.FullName ?? '—',
    email: item?.email ?? item?.Email ?? '—',
    affiliateCode: item?.affiliateCode ?? item?.AffiliateCode ?? '—',
    totalClients: Number(item?.totalClients ?? item?.TotalClients ?? 0) || 0,
    totalLots: Number(item?.totalLots ?? item?.TotalLots ?? 0) || 0,
    createdDateTime: item?.createdDateTime ?? item?.CreatedDateTime ?? null,
    isActive: isActiveRaw === true || isActiveRaw === 1 || String(isActiveRaw).toLowerCase() === 'true',
  };
};

const mapSubPartnerCommissionRow = (item) => ({
  id: item?.affiliateCommissionId ?? item?.AffiliateCommissionId,
  tradeId: item?.tradeId ?? item?.TradeId,
  clientCode: item?.clientCode ?? item?.ClientCode ?? '—',
  levelId: item?.affiliateLevelId ?? item?.AffiliateLevelId ?? '—',
  amount: toAmount(item?.commissionAmount ?? item?.CommissionAmount),
  createdDatetime: item?.createdDatetime ?? item?.CreatedDatetime ?? null,
});

const mapSubPartnerTrade = (item) => ({
  symbol: item?.symbol ?? item?.Symbol ?? '—',
  loginId: item?.loginId ?? item?.LoginId ?? '—',
  openTime: item?.openTime ?? item?.OpenTime ?? null,
  closeTime: item?.closeTime ?? item?.CloseTime ?? null,
  openPrice: item?.openPrice ?? item?.OpenPrice,
  closePrice: item?.closePrice ?? item?.ClosePrice,
  tradeType: item?.tradeType ?? item?.TradeType ?? '—',
  volume: item?.volume ?? item?.Volume,
  profit: item?.profit ?? item?.Profit,
});

const toApiDate = (dateStr) => {
  if (!dateStr) return '';
  // Keep calendar day as-is (YYYY-MM-DD). Do NOT use toISOString() —
  // that shifts the day backward in UTC+ timezones (e.g. PK UTC+5).
  // API inclusive end: ToDate.Date.AddDays(1).
  const match = String(dateStr).trim().match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? `${match[1]}T00:00:00` : '';
};

const toLots = (volume) => {
  const v = parseFloat(volume);
  if (!v || Number.isNaN(v)) return 0;
  return v >= 100 ? v / 10000 : v;
};

/** API summary `totalLots` is already in lots — do not apply volume conversion. */
const formatWidgetLots = (lots) =>
  toAmount(lots).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 4 });

const KNOWN_SOURCES = ['Direct Clients', 'Sub Partners', 'Campaign Bonus', 'Other Bonus'];

const normalizeSource = (source) => {
  const s = (source || '').toString().toLowerCase();
  if (s.includes('sub')) return 'Sub Partners';
  if (s.includes('campaign')) return 'Campaign Bonus';
  if (s.includes('bonus')) return 'Other Bonus';
  return 'Direct Clients';
};

const resolveSource = (source) => {
  const trimmed = (source || '').toString().trim();
  if (!trimmed) return '—';
  const exact = KNOWN_SOURCES.find((label) => label.toLowerCase() === trimmed.toLowerCase());
  return exact || normalizeSource(trimmed);
};

const transformCommission = (c, index = 0) => {
  const lots = toLots(c.volume ?? c.lots ?? c.quantity ?? c.size);
  const amount = toAmount(c.profit ?? c.amount ?? c.commissionAmount ?? c.commission);
  const rate = lots > 0 ? amount / lots : toAmount(c.rate ?? c.commissionRate);
  const source = resolveSource(c.source ?? c.commissionSource ?? c.commissionType ?? c.type);
  const openTime = c.openTime ?? c.startTime ?? null;
  const closeTime = c.closeTime ?? c.endTime ?? null;
  const tradeTime = openTime ?? closeTime ?? c.createdDatetime ?? c.date ?? c.createdDate;
  const symbol = c.symbol ?? c.tradingSymbol ?? '';

  return {
    id: c.id ?? c.commissionId ?? c.dealId ?? c.tradeId ?? `${c.loginId ?? 'row'}-${tradeTime}-${symbol}-${index}`,
    raw: c,
    openTime,
    closeTime,
    date: tradeTime,
    source,
    description: symbol || c.description || 'Trading Commission',
    lots,
    rate,
    amount,
    accountNo: c.loginId ?? c.clientLoginId ?? c.userId ?? c.clientId ?? '—',
    clientCode: c.clientCode ?? c.ClientCode ?? c.userCode ?? c.UserCode ?? '—',
  };
};

const EMPTY_BREAKDOWN = {
  segments: [],
  list: [],
  donutSegments: [],
  total: 0,
};

const SectionLoader = ({ minHeight = 'py-10' }) => (
  <div className={`flex justify-center items-center ${minHeight}`}>
    <NewtonsCradleLoader size="28px" />
  </div>
);

const SummaryCard = ({ title, value, change, subtitle, icon: Icon, iconBg, iconColor, isDarkMode, loading = false }) => (
  <div className={`rounded-2xl border p-4 shadow-sm ${isDarkMode ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100'}`}>
    <div className="flex items-start justify-between gap-3">
      <div className="flex-1 min-w-0">
        <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{title}</p>
        {loading ? (
          <div className="mt-3">
            <NewtonsCradleLoader size="24px" />
          </div>
        ) : (
          <p className={`text-xl font-bold mt-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{value}</p>
        )}
        {!loading && subtitle && (
          <p className={`text-[11px] mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{subtitle}</p>
        )}
        {!loading && change && (
          <p className="text-[11px] mt-1 font-medium text-emerald-600 flex items-center gap-0.5">
            <TrendingUp className="w-3 h-3" />
            {change}
          </p>
        )}
      </div>
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${iconBg}`}>
        <Icon className={`w-5 h-5 ${iconColor}`} />
      </div>
    </div>
  </div>
);

const TREND_PIE_COLORS = ['#8b0000', '#6366F1', '#F97316', '#10B981', '#EC4899', '#64748B', '#0EA5E9', '#A855F7'];

const CommissionTrendChart = ({ isDarkMode, monthwiseData }) => {
  if (!monthwiseData?.labels?.length) {
    return (
      <p className={`text-sm text-center py-10 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
        No commission trend data available
      </p>
    );
  }

  const { labels } = monthwiseData;
  const amounts = monthwiseData.amounts ?? monthwiseData.earned ?? [];
  const w = 480;
  const h = 160;
  const pad = { l: 36, r: 12, t: 12, b: 28 };
  const cw = w - pad.l - pad.r;
  const ch = h - pad.t - pad.b;
  const max = Math.max(...amounts, 1) * 1.1;
  const toY = (v) => pad.t + ch - (v / max) * ch;
  const toX = (i) => pad.l + (i / Math.max(labels.length - 1, 1)) * cw;
  const line = amounts.map((v, i) => `${toX(i)},${toY(v)}`).join(' ');
  const grid = isDarkMode ? '#374151' : '#F3F4F6';

  return (
    <div>
      <div className="flex flex-wrap items-center gap-4 mb-3 text-[11px]">
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: THEME }} /> Commission</span>
      </div>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-40">
        {[0, 1, 2, 3].map((i) => (
          <line key={i} x1={pad.l} y1={pad.t + (i / 3) * ch} x2={pad.l + cw} y2={pad.t + (i / 3) * ch} stroke={grid} />
        ))}
        {labels.map((m, i) => (
          <text key={`${m}-${i}`} x={toX(i)} y={h - 6} textAnchor="middle" fill={isDarkMode ? '#9CA3AF' : '#6B7280'} fontSize="9">
            {m}
          </text>
        ))}
        <polyline fill="none" stroke={THEME} strokeWidth="2.5" strokeLinecap="round" points={line} />
      </svg>
    </div>
  );
};

const CommissionTrendPieChart = ({ monthwiseData, isDarkMode }) => {
  const segments = useMemo(() => {
    if (!monthwiseData?.labels?.length) return [];
    const amounts = monthwiseData.amounts ?? [];
    const percentages = monthwiseData.percentages ?? [];
    return monthwiseData.labels
      .map((label, i) => ({
        label,
        value: toAmount(amounts[i]),
        pct: percentages[i],
        color: TREND_PIE_COLORS[i % TREND_PIE_COLORS.length],
      }))
      .filter((seg) => seg.value > 0);
  }, [monthwiseData]);

  const total = segments.reduce((sum, seg) => sum + seg.value, 0);

  if (!segments.length) {
    return (
      <p className={`text-sm text-center py-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
        No monthly commission data for chart
      </p>
    );
  }

  const circumference = 2 * Math.PI * 42;
  let offset = 0;
  const sum = total || 1;

  return (
    <div className="flex flex-col sm:flex-row items-center gap-5 sm:gap-6">
      <div className="relative w-32 h-32 shrink-0">
        <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
          <circle cx="50" cy="50" r="42" fill="none" stroke={isDarkMode ? '#374151' : '#F3F4F6'} strokeWidth="10" />
          {segments.map((seg, i) => {
            const len = (seg.value / sum) * circumference;
            const dash = `${len} ${circumference - len}`;
            const el = (
              <circle
                key={i}
                cx="50"
                cy="50"
                r="42"
                fill="none"
                stroke={seg.color}
                strokeWidth="10"
                strokeDasharray={dash}
                strokeDashoffset={-offset}
              />
            );
            offset += len;
            return el;
          })}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-2">
          <span className={`text-xs font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            {formatWidgetCurrency(total)}
          </span>
          <span className={`text-[10px] ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Total</span>
        </div>
      </div>
      <div className="flex-1 w-full space-y-2 min-w-0">
        {segments.map((seg) => (
          <div key={seg.label} className="flex items-center justify-between gap-3 text-xs">
            <span className={`flex items-center gap-2 min-w-0 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: seg.color }} />
              <span className="truncate">{seg.label}</span>
            </span>
            <span className={`shrink-0 font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {formatWidgetCurrency(seg.value)}
              {seg.pct != null && (
                <span className={`ml-1.5 font-normal ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  ({seg.pct}%)
                </span>
              )}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const CommissionBreakdownDonut = ({ segments, total, isDarkMode }) => {
  const circumference = 2 * Math.PI * 42;
  const sum = segments.reduce((s, seg) => s + seg.value, 0) || 1;
  let offset = 0;

  return (
    <div className="relative w-36 h-36 mx-auto">
      <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
        <circle cx="50" cy="50" r="42" fill="none" stroke={isDarkMode ? '#374151' : '#F3F4F6'} strokeWidth="10" />
        {segments.map((seg, i) => {
          const len = (seg.value / sum) * circumference;
          const dash = `${len} ${circumference - len}`;
          const el = (
            <circle
              key={i}
              cx="50"
              cy="50"
              r="42"
              fill="none"
              stroke={seg.color}
              strokeWidth="10"
              strokeDasharray={dash}
              strokeDashoffset={-offset}
              strokeLinecap="butt"
            />
          );
          offset += len;
          return el;
        })}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-2">
        <span className={`text-sm font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{formatCurrency(total)}</span>
        <span className={`text-[10px] ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Total</span>
      </div>
    </div>
  );
};

const Commissions = ({ view = 'overview' }) => {
  const { isDarkMode } = useTheme();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const initialClientCode = (searchParams.get('clientCode') || '').trim();
  const isOverview = view === 'overview';
  const isList = view === 'list';
  const isOldList = view === 'old-list';
  const isListView = isList || isOldList;

  const [activeTab, setActiveTab] = useState('All');
  const [allCommissions, setAllCommissions] = useState([]);
  const [isListLoading, setIsListLoading] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [isDashboardLoading, setIsDashboardLoading] = useState(false);
  const [dataStartDate, setDataStartDate] = useState('');
  const [pagination, setPagination] = useState({
    totalRecords: 0,
    totalPages: 1,
    pageNumber: 1,
    pageSize: 10,
    totalClients: 0,
    totalActiveClients: 0,
    totalLots: 0,
    totalProfit: 0,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState('10');
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [clientCode, setClientCode] = useState(initialClientCode);
  const [accountCode, setAccountCode] = useState('');
  const [appliedClientCode, setAppliedClientCode] = useState(initialClientCode);
  const [appliedAccountCode, setAppliedAccountCode] = useState('');
  const [filters, setFilters] = useState({ fromDate: '', toDate: '' });
  const [appliedFilters, setAppliedFilters] = useState({ fromDate: '', toDate: '' });
  const [isExportingOldData, setIsExportingOldData] = useState(false);
  const [subPartners, setSubPartners] = useState([]);
  const [isSubPartnersLoading, setIsSubPartnersLoading] = useState(false);
  const isSubPartnersTab = isListView && activeTab === 'Sub Partners';
  const isSubPartnerCommissionsTab = isList && activeTab === SUB_PARTNER_COMMISSIONS_TAB;
  const [spCommissions, setSpCommissions] = useState([]);
  const [isSpCommissionsLoading, setIsSpCommissionsLoading] = useState(false);
  const [spPagination, setSpPagination] = useState({ totalRecords: 0, totalPages: 1 });
  const [spExpanded, setSpExpanded] = useState(new Set());
  // tradeId -> { loading, data }
  const [spTrades, setSpTrades] = useState({});

  const apiPageSize = resolveApiPageSize(pageSize);
  const apiFromDate = toApiDate(appliedFilters.fromDate);
  const apiToDate = toApiDate(appliedFilters.toDate);
  const listFetchKey = isList
    ? `${location.pathname}|${currentPage}|${apiPageSize}|${apiFromDate}|${apiToDate}|${appliedClientCode}|${appliedAccountCode}`
    : null;
  const oldListFetchKey = isOldList
    ? `${location.pathname}|${currentPage}|${apiPageSize}|${apiFromDate}|${apiToDate}|${appliedClientCode}|${appliedAccountCode}`
    : null;
  const dashboardFetchKey = isOverview ? `${location.pathname}|earning-dashboard` : null;

  const fetchEarningDashboard = useCallback(async ({ cancelled }) => {
    setIsDashboardLoading(true);
    try {
      const response = await apiService.getEarningDashboard();
      if (cancelled()) return;
      setDashboard(response?.success ? parseEarningDashboard(response) : null);
    } catch {
      if (!cancelled()) setDashboard(null);
    } finally {
      if (!cancelled()) setIsDashboardLoading(false);
    }
  }, []);

  const fetchCommissionHistory = useCallback(async ({ cancelled }) => {
    setIsListLoading(true);
    try {
      const response = await apiService.getCommissionHistory({
        pageNumber: currentPage,
        pageSize: apiPageSize,
        fromDate: apiFromDate,
        toDate: apiToDate,
        searchTerm: '',
        clientCode: appliedClientCode,
        accountCode: appliedAccountCode,
      });
      if (cancelled()) return;

      const list = extractCommissionHistoryList(response).map((item, index) => transformCommission(item, index));
      const meta = extractCommissionHistoryPagination(response, apiPageSize);
      setAllCommissions(list);
      setPagination(meta);
      // Start date from the backend setting (empty = no limit): dates before it are disabled in the filter.
      setDataStartDate(String(response?.data?.dataStartDate ?? response?.data?.DataStartDate ?? '').slice(0, 10));
    } catch {
      if (!cancelled()) {
        setAllCommissions([]);
        setPagination({
          totalRecords: 0,
          totalPages: 1,
          pageNumber: currentPage,
          pageSize: apiPageSize,
          totalClients: 0,
          totalActiveClients: 0,
          totalLots: 0,
          totalProfit: 0,
        });
      }
    } finally {
      if (!cancelled()) setIsListLoading(false);
    }
  }, [currentPage, apiPageSize, apiFromDate, apiToDate, appliedClientCode, appliedAccountCode]);

  const fetchOldCommissionHistory = useCallback(async ({ cancelled }) => {
    setIsListLoading(true);
    try {
      const response = await apiService.getOldCommissionHistory({
        pageNumber: currentPage,
        pageSize: apiPageSize,
        fromDate: apiFromDate,
        toDate: apiToDate,
        searchTerm: '',
        clientCode: appliedClientCode,
        accountCode: appliedAccountCode,
      });
      if (cancelled()) return;

      const list = extractCommissionHistoryList(response).map((item, index) => transformCommission(item, index));
      const meta = extractCommissionHistoryPagination(response, apiPageSize);
      setAllCommissions(list);
      setPagination(meta);
    } catch {
      if (!cancelled()) {
        setAllCommissions([]);
        setPagination({
          totalRecords: 0,
          totalPages: 1,
          pageNumber: currentPage,
          pageSize: apiPageSize,
          totalClients: 0,
          totalActiveClients: 0,
          totalLots: 0,
          totalProfit: 0,
        });
      }
    } finally {
      if (!cancelled()) setIsListLoading(false);
    }
  }, [currentPage, apiPageSize, apiFromDate, apiToDate, appliedClientCode, appliedAccountCode]);

  const fetchSubPartners = useCallback(async ({ cancelled }) => {
    setIsSubPartnersLoading(true);
    try {
      const response = await apiService.getSubPartnersList();
      if (cancelled()) return;
      const raw = response?.data?.result ?? response?.data ?? response?.result ?? [];
      setSubPartners(response?.success && Array.isArray(raw) ? raw.map(mapSubPartnerRow) : []);
    } catch {
      if (!cancelled()) setSubPartners([]);
    } finally {
      if (!cancelled()) setIsSubPartnersLoading(false);
    }
  }, []);

  const fetchSubPartnerCommissions = useCallback(async ({ cancelled }) => {
    setIsSpCommissionsLoading(true);
    try {
      const response = await apiService.getSubPartnerCommissions({
        pageNumber: currentPage,
        pageSize: apiPageSize,
        fromDate: apiFromDate,
        toDate: apiToDate,
        clientCode: appliedClientCode,
        accountCode: appliedAccountCode,
      });
      if (cancelled()) return;
      const data = response?.data ?? {};
      const list = data.data ?? data.Data ?? [];
      setSpCommissions(response?.success && Array.isArray(list) ? list.map(mapSubPartnerCommissionRow) : []);
      setSpPagination({
        totalRecords: Number(data.totalRecords ?? data.TotalRecords) || 0,
        totalPages: Number(data.totalPages ?? data.TotalPages) || 1,
      });
    } catch {
      if (!cancelled()) {
        setSpCommissions([]);
        setSpPagination({ totalRecords: 0, totalPages: 1 });
      }
    } finally {
      if (!cancelled()) setIsSpCommissionsLoading(false);
    }
  }, [currentPage, apiPageSize, apiFromDate, apiToDate, appliedClientCode, appliedAccountCode]);

  const toggleSpRow = async (row) => {
    const opening = !spExpanded.has(row.id);
    setSpExpanded((prev) => {
      const next = new Set(prev);
      if (opening) next.add(row.id);
      else next.delete(row.id);
      return next;
    });
    if (!opening || spTrades[row.tradeId]) return;

    setSpTrades((prev) => ({ ...prev, [row.tradeId]: { loading: true, data: null } }));
    const response = await apiService.getSubPartnerCommissionTrade(row.tradeId);
    setSpTrades((prev) => ({
      ...prev,
      [row.tradeId]: { loading: false, data: response?.success && response.data ? mapSubPartnerTrade(response.data) : null },
    }));
  };

  const spListKey = isSubPartnerCommissionsTab
    ? `${location.pathname}|sp-commissions|${currentPage}|${apiPageSize}|${apiFromDate}|${apiToDate}|${appliedClientCode}|${appliedAccountCode}`
    : null;
  useFetchOnceByKey(spListKey, fetchSubPartnerCommissions, isSubPartnerCommissionsTab);
  useFetchOnceByKey(isSubPartnersTab ? 'earning-list-sub-partners' : null, fetchSubPartners, isSubPartnersTab);
  useFetchOnceByKey(dashboardFetchKey, fetchEarningDashboard, isOverview);
  useFetchOnceByKey(listFetchKey, fetchCommissionHistory, isList);
  useFetchOnceByKey(oldListFetchKey, fetchOldCommissionHistory, isOldList);

  useEffect(() => {
    if (!isListView) setAllCommissions([]);
  }, [isListView]);

  const filteredCommissions = useMemo(() => {
    if (activeTab === 'All') return allCommissions;
    return allCommissions.filter((c) => c.source === activeTab);
  }, [allCommissions, activeTab]);

  const breakdown = dashboard?.breakdown ?? EMPTY_BREAKDOWN;
  const monthwiseData = dashboard?.trend ?? null;

  const totalPages = Math.max(1, Number((isSubPartnerCommissionsTab ? spPagination : pagination).totalPages) || 1);
  const effectivePageSize = apiPageSize;
  const totalRecords = isSubPartnerCommissionsTab
    ? spPagination.totalRecords
    : pagination.totalRecords || allCommissions.length;
  const showingFrom = totalRecords === 0 ? 0 : (currentPage - 1) * effectivePageSize + 1;
  const showingTo = Math.min(currentPage * effectivePageSize, totalRecords);
  const paginatedRows = filteredCommissions;

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, pageSize]);

  const toggleRow = (id) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleApplyFilters = () => {
    setAppliedFilters({ ...filters });
    setAppliedClientCode(clientCode.trim());
    setAppliedAccountCode(accountCode.trim());
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    const empty = { fromDate: '', toDate: '' };
    setFilters(empty);
    setAppliedFilters(empty);
    setClientCode('');
    setAccountCode('');
    setAppliedClientCode('');
    setAppliedAccountCode('');
    setCurrentPage(1);
  };

  const handleExportOldDataExcel = async () => {
    setIsExportingOldData(true);
    try {
      const result = await apiService.exportOldDataHistoryExcel({
        pageNumber: 0,
        pageSize: 0,
        fromDate: apiFromDate,
        toDate: apiToDate,
        searchTerm: '',
        clientCode: appliedClientCode,
        accountCode: appliedAccountCode,
      });

      if (result.downloadUrl) {
        const a = document.createElement('a');
        a.href = result.downloadUrl;
        a.download = result.fileName || 'old-earning-data.xlsx';
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
        a.click();
        return;
      }

      if (result.blob) {
        const url = URL.createObjectURL(result.blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = result.fileName || 'old-earning-data.xlsx';
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch {
      /* ignore */
    } finally {
      setIsExportingOldData(false);
    }
  };

  const card = isDarkMode ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100';
  const text = isDarkMode ? 'text-white' : 'text-gray-900';
  const muted = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  const inputCls = `w-full rounded-xl border px-3 py-2.5 text-sm outline-none transition focus:border-red-800 focus:ring-2 focus:ring-red-800/15 ${
    isDarkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-200 text-gray-900'
  }`;

  const kpiTotal = dashboard ? formatWidgetCurrency(dashboard.totalEarned) : '—';
  const kpiPending = dashboard ? formatWidgetCurrency(dashboard.pending) : '—';
  const kpiPaid = dashboard ? formatWidgetCurrency(dashboard.paid) : '—';
  const tilesSince = dashboard?.tilesStartDate ? `Since ${formatShortDate(dashboard.tilesStartDate)}` : '';
  const lastWithdrawal = dashboard?.lastWithdrawal ?? null;
  const kpiLastWithdrawal = lastWithdrawal ? formatWidgetCurrency(lastWithdrawal.amount) : '—';
  const lastWithdrawalSubtitle = lastWithdrawal
    ? [formatShortDate(lastWithdrawal.date), lastWithdrawal.status].filter(Boolean).join(' · ')
    : dashboard
      ? 'No withdrawals yet'
      : '';
  const earningSummary = dashboard?.earningSummary;
  const breakdownDonut = breakdown.donutSegments?.length ? breakdown.donutSegments : breakdown.segments;
  const breakdownList = breakdown.list?.length ? breakdown.list : breakdown.segments;
  const breakdownDonutTotal = dashboard?.totalEarned ?? breakdown.total;

  return (
    <div className={`min-h-full ${isDarkMode ? 'bg-black' : 'bg-[#F4F5F7]'}`}>
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 py-5 sm:py-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
          <div>
            <h1 className={`text-2xl font-bold ${text}`}>
              {isOverview ? 'Earning Dashboard' : isOldList ? 'Old Earning Data' : 'Earning List'}
            </h1>
            <p className={`text-sm mt-1 ${muted}`}>
              <Link to="/" className="hover:text-red-800">Dashboard</Link>
              <span className="mx-1.5">›</span>
              <span>Earning</span>
              <span className="mx-1.5">›</span>
              <span>{isOverview ? 'Dashboard' : isOldList ? 'Old Data' : 'List'}</span>
            </p>
          </div>
          {isList && (
            <Link
              to="/earnings/list/old"
              className="inline-flex items-center justify-center px-4 py-2.5 rounded-xl text-sm font-semibold text-white shrink-0"
              style={{ backgroundColor: THEME }}
            >
              Get Old Data
            </Link>
          )}
          {isOldList && (
            <Link
              to="/earnings/list"
              className={`inline-flex items-center justify-center px-4 py-2.5 rounded-xl text-sm font-semibold border shrink-0 ${
                isDarkMode ? 'border-gray-700 text-gray-200 hover:bg-gray-800' : 'border-gray-200 text-gray-700 hover:bg-gray-50'
              }`}
            >
              Current Earning List
            </Link>
          )}
        </div>

        {isListView && (
          <div className={`rounded-2xl border p-4 sm:p-5 shadow-sm mb-6 ${card}`}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
              <div>
                <label className={`block text-xs font-medium mb-1.5 ${muted}`}>From Date</label>
                <input type="date" value={filters.fromDate} min={dataStartDate || undefined} onChange={(e) => setFilters((f) => ({ ...f, fromDate: e.target.value }))} className={inputCls} />
              </div>
              <div>
                <label className={`block text-xs font-medium mb-1.5 ${muted}`}>To Date</label>
                <input type="date" value={filters.toDate} min={filters.fromDate || dataStartDate || undefined} onChange={(e) => setFilters((f) => ({ ...f, toDate: e.target.value }))} className={inputCls} />
              </div>
              <div>
                <label className={`block text-xs font-medium mb-1.5 ${muted}`}>Client Code</label>
                <input type="text" value={clientCode} onChange={(e) => setClientCode(e.target.value)} placeholder="e.g. A-UC-07105" className={inputCls} />
              </div>
              <div>
                <label className={`block text-xs font-medium mb-1.5 ${muted}`}>Account Code</label>
                <input type="text" value={accountCode} onChange={(e) => setAccountCode(e.target.value)} placeholder="Trading account / login" className={inputCls} />
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={handleApplyFilters} className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white" style={{ backgroundColor: THEME }}>
                  Apply
                </button>
                <button type="button" onClick={handleResetFilters} className={`flex-1 py-2.5 rounded-xl text-sm font-medium border ${isDarkMode ? 'border-gray-700 text-gray-300 hover:bg-gray-800' : 'border-gray-200 text-gray-700 hover:bg-gray-50'}`}>
                  Reset
                </button>
                {isOldList && (
                  <button
                    type="button"
                    onClick={handleExportOldDataExcel}
                    disabled={isExportingOldData}
                    className={`flex-1 py-2.5 rounded-xl text-sm font-medium border transition disabled:opacity-60 ${
                      isDarkMode
                        ? 'border-red-800 text-red-300 hover:bg-red-950'
                        : 'border-red-200 text-red-800 hover:bg-red-50'
                    }`}
                  >
                    {isExportingOldData ? 'Exporting…' : 'Export Excel'}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {isList && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <SummaryCard
              title="Total Clients"
              value={pagination.totalClients.toLocaleString('en-US')}
              icon={Users}
              iconBg="bg-blue-50"
              iconColor="text-blue-600"
              isDarkMode={isDarkMode}
            />
            <SummaryCard
              title="Total Active Clients"
              value={pagination.totalActiveClients.toLocaleString('en-US')}
              icon={Users}
              iconBg="bg-red-50"
              iconColor="text-red-800"
              isDarkMode={isDarkMode}
            />
            <SummaryCard
              title="Total Lots"
              value={formatWidgetLots(pagination.totalLots)}
              icon={BarChart3}
              iconBg="bg-violet-50"
              iconColor="text-violet-600"
              isDarkMode={isDarkMode}
            />
            <SummaryCard
              title="Total Profit"
              value={formatWidgetCurrency(pagination.totalProfit)}
              icon={DollarSign}
              iconBg="bg-emerald-50"
              iconColor="text-emerald-600"
              isDarkMode={isDarkMode}
            />
          </div>
        )}

        {isOverview && (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
          <SummaryCard title="Total Commission Earned" value={kpiTotal} subtitle={tilesSince} loading={isDashboardLoading} icon={DollarSign} iconBg="bg-red-50" iconColor="text-red-800" isDarkMode={isDarkMode} />
          <SummaryCard title="Pending Commission" value={kpiPending} loading={isDashboardLoading} icon={Clock} iconBg="bg-amber-50" iconColor="text-amber-600" isDarkMode={isDarkMode} />
          <SummaryCard title="Paid Commission" value={kpiPaid} subtitle={tilesSince} loading={isDashboardLoading} icon={Wallet} iconBg="bg-emerald-50" iconColor="text-emerald-600" isDarkMode={isDarkMode} />
          <SummaryCard title="Last Withdrawal" value={kpiLastWithdrawal} subtitle={lastWithdrawalSubtitle} loading={isDashboardLoading} icon={ArrowDownCircle} iconBg="bg-blue-50" iconColor="text-blue-600" isDarkMode={isDarkMode} />
        </div>
        )}

        <div className={`grid grid-cols-1 ${isOverview ? 'xl:grid-cols-[1fr_300px]' : ''} gap-6`}>
          {/* Main column */}
          <div className="space-y-6">
            {isOverview && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className={`rounded-2xl border p-5 shadow-sm ${card}`}>
                <h2 className={`text-sm font-semibold mb-4 ${text}`}>Commission Trend</h2>
                {isDashboardLoading ? (
                  <SectionLoader />
                ) : (
                  <>
                    <CommissionTrendChart isDarkMode={isDarkMode} monthwiseData={monthwiseData} />
                    <div className={`mt-5 pt-5 border-t ${isDarkMode ? 'border-gray-800' : 'border-gray-100'}`}>
                      <h3 className={`text-xs font-semibold mb-4 ${muted}`}>Monthly Distribution</h3>
                      <CommissionTrendPieChart isDarkMode={isDarkMode} monthwiseData={monthwiseData} />
                    </div>
                  </>
                )}
              </div>
              <div className={`rounded-2xl border p-5 shadow-sm ${card}`}>
                <h2 className={`text-sm font-semibold mb-4 ${text}`}>Commission Breakdown</h2>
                {isDashboardLoading ? (
                  <SectionLoader />
                ) : breakdownList.length > 0 ? (
                  <>
                    <CommissionBreakdownDonut segments={breakdownDonut} total={breakdownDonutTotal} isDarkMode={isDarkMode} />
                    <div className="mt-4 space-y-2 max-h-52 overflow-y-auto pr-1">
                      {breakdownList.map((seg) => (
                        <div key={seg.label} className="flex items-center justify-between text-xs gap-2">
                          <span className={`flex items-center gap-2 min-w-0 ${muted}`}>
                            <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: seg.color }} />
                            <span className="truncate">{seg.label}</span>
                          </span>
                          <span className={`shrink-0 text-right ${text}`}>
                            {formatWidgetCurrency(seg.value)}
                            {seg.pct != null && <span className={`ml-1.5 ${muted}`}>({seg.pct}%)</span>}
                          </span>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <p className={`text-sm text-center py-10 ${muted}`}>No breakdown data available</p>
                )}
              </div>
            </div>
            )}

            {isOverview && (
            <div className="flex justify-end">
              <Link
                to="/earnings/list"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-red-800 hover:underline"
              >
                View Earning List
                <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            )}

            {isListView && (
            <div className={`rounded-2xl border shadow-sm overflow-hidden ${card}`}>
              <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-800">
                <div className="flex flex-wrap gap-1">
                  {SOURCE_TABS.map((tab) => (
                    <button
                      key={tab}
                      type="button"
                      onClick={() => setActiveTab(tab)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                        activeTab === tab
                          ? 'text-white'
                          : isDarkMode
                            ? 'text-gray-400 hover:bg-gray-800'
                            : 'text-gray-600 hover:bg-gray-100'
                      }`}
                      style={activeTab === tab ? { backgroundColor: THEME } : {}}
                    >
                      {tab}
                    </button>
                  ))}
                </div>
              </div>

              {isSubPartnerCommissionsTab ? (
                isSpCommissionsLoading ? (
                  <div className="flex justify-center py-16">
                    <NewtonsCradleLoader />
                  </div>
                ) : spCommissions.length === 0 ? (
                  <div className={`text-center py-16 px-4 ${muted}`}>
                    <p className="text-sm">No sub partner commissions found.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[760px]">
                      <thead>
                        <tr className={`text-left text-xs font-medium border-b ${isDarkMode ? 'border-gray-800 text-gray-400' : 'border-gray-100 text-gray-500'}`}>
                          <th className="px-4 py-3 w-10" />
                          <th className="px-3 py-3">Trade ID</th>
                          <th className="px-3 py-3">Client Code</th>
                          <th className="px-3 py-3">Level</th>
                          <th className="px-3 py-3">Date</th>
                          <th className="px-3 py-3">Commission</th>
                        </tr>
                      </thead>
                      <tbody className={`divide-y ${isDarkMode ? 'divide-gray-800' : 'divide-gray-50'}`}>
                        {spCommissions.map((row) => {
                          const expanded = spExpanded.has(row.id);
                          const trade = spTrades[row.tradeId];
                          return (
                            <React.Fragment key={row.id}>
                              <tr className={isDarkMode ? 'hover:bg-gray-800/50' : 'hover:bg-gray-50/80'}>
                                <td className="px-4 py-3.5">
                                  <button
                                    type="button"
                                    onClick={() => toggleSpRow(row)}
                                    className={`inline-flex items-center justify-center w-7 h-7 rounded-lg border transition ${
                                      isDarkMode ? 'border-gray-700 text-gray-400 hover:bg-gray-800' : 'border-gray-200 text-gray-500 hover:bg-gray-50'
                                    }`}
                                    aria-label={expanded ? 'Collapse' : 'Expand'}
                                  >
                                    {expanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                                  </button>
                                </td>
                                <td className={`px-3 py-3.5 text-sm ${text}`}>{row.tradeId}</td>
                                <td className={`px-3 py-3.5 text-sm ${text}`}>{row.clientCode}</td>
                                <td className={`px-3 py-3.5 text-sm ${text}`}>{row.levelId}</td>
                                <td className={`px-3 py-3.5 text-sm ${text}`}>{formatOpenTime(row.createdDatetime)}</td>
                                <td className={`px-3 py-3.5 text-sm font-semibold ${text}`}>{formatCurrency4(row.amount)}</td>
                              </tr>
                              {expanded && (
                                <tr className={isDarkMode ? 'bg-gray-800/30' : 'bg-gray-50/80'}>
                                  <td colSpan={6} className="px-5 py-4">
                                    {!trade || trade.loading ? (
                                      <SectionLoader minHeight="py-4" />
                                    ) : !trade.data ? (
                                      <p className={`text-xs text-center ${muted}`}>No trade details found.</p>
                                    ) : (
                                      <div className={`rounded-xl border overflow-x-auto ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                                        <table className="w-full min-w-[720px]">
                                          <thead>
                                            <tr className={`text-left text-xs font-medium border-b ${isDarkMode ? 'border-gray-700 text-gray-400' : 'border-gray-200 text-gray-500'}`}>
                                              <th className="px-3 py-2">Symbol</th>
                                              <th className="px-3 py-2">Account No</th>
                                              <th className="px-3 py-2">Type</th>
                                              <th className="px-3 py-2">Lots</th>
                                              <th className="px-3 py-2">Open Time</th>
                                              <th className="px-3 py-2">Close Time</th>
                                              <th className="px-3 py-2">Open Price</th>
                                              <th className="px-3 py-2">Close Price</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            <tr className="text-xs">
                                              <td className={`px-3 py-2 font-medium ${text}`}>{trade.data.symbol}</td>
                                              <td className={`px-3 py-2 ${text}`}>{trade.data.loginId}</td>
                                              <td className={`px-3 py-2 ${text}`}>{trade.data.tradeType}</td>
                                              <td className={`px-3 py-2 ${text}`}>{trade.data.volume != null ? Number(trade.data.volume).toFixed(2) : '—'}</td>
                                              <td className={`px-3 py-2 ${text}`}>{formatOpenTime(trade.data.openTime)}</td>
                                              <td className={`px-3 py-2 ${text}`}>{formatOpenTime(trade.data.closeTime)}</td>
                                              <td className={`px-3 py-2 ${text}`}>{formatPrice(trade.data.openPrice)}</td>
                                              <td className={`px-3 py-2 ${text}`}>{formatPrice(trade.data.closePrice)}</td>
                                            </tr>
                                          </tbody>
                                        </table>
                                      </div>
                                    )}
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )
              ) : isSubPartnersTab ? (
                isSubPartnersLoading ? (
                  <div className="flex justify-center py-16">
                    <NewtonsCradleLoader />
                  </div>
                ) : subPartners.length === 0 ? (
                  <div className={`text-center py-16 px-4 ${muted}`}>
                    <p className="text-sm">No sub partners found.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[760px]">
                      <thead>
                        <tr className={`text-left text-xs font-medium border-b ${isDarkMode ? 'border-gray-800 text-gray-400' : 'border-gray-100 text-gray-500'}`}>
                          <th className="px-5 py-3 w-14">S.No</th>
                          <th className="px-3 py-3">Name</th>
                          <th className="px-3 py-3">Email</th>
                          <th className="px-3 py-3">Partner Code</th>
                          <th className="px-3 py-3">Total Clients</th>
                          <th className="px-3 py-3">Total Lots</th>
                          <th className="px-3 py-3">Joined</th>
                          <th className="px-3 py-3">Status</th>
                        </tr>
                      </thead>
                      <tbody className={`divide-y ${isDarkMode ? 'divide-gray-800' : 'divide-gray-50'}`}>
                        {subPartners.map((sp, idx) => (
                          <tr key={sp.id} className={isDarkMode ? 'hover:bg-gray-800/50' : 'hover:bg-gray-50/80'}>
                            <td className={`px-5 py-3.5 text-sm ${muted}`}>{idx + 1}</td>
                            <td className={`px-3 py-3.5 text-sm font-medium ${text}`}>{sp.fullName}</td>
                            <td className={`px-3 py-3.5 text-sm ${text}`}>{sp.email}</td>
                            <td className={`px-3 py-3.5 text-sm ${text}`}>{sp.affiliateCode}</td>
                            <td className={`px-3 py-3.5 text-sm ${text}`}>{sp.totalClients.toLocaleString('en-US')}</td>
                            <td className={`px-3 py-3.5 text-sm ${text}`}>{formatWidgetLots(sp.totalLots)}</td>
                            <td className={`px-3 py-3.5 text-sm ${text}`}>{formatShortDate(sp.createdDateTime) || '—'}</td>
                            <td className="px-3 py-3.5">
                              <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${sp.isActive ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}>
                                {sp.isActive ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
              ) : isListLoading ? (
                <div className="flex justify-center py-16">
                  <NewtonsCradleLoader />
                </div>
              ) : paginatedRows.length === 0 ? (
                <div className={`text-center py-16 px-4 ${muted}`}>
                  <p className="text-sm">
                    {allCommissions.length === 0 && !appliedFilters.fromDate && !appliedFilters.toDate && !appliedClientCode && !appliedAccountCode
                      ? isOldList
                        ? 'No old commission records found.'
                        : 'No commission records found.'
                      : 'No records match your filters.'}
                  </p>
                  {(appliedFilters.fromDate || appliedFilters.toDate || appliedClientCode || appliedAccountCode) && (
                    <button type="button" onClick={handleResetFilters} className="mt-3 text-sm font-medium text-red-800 hover:underline">
                      Reset filters
                    </button>
                  )}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[860px]">
                    <thead>
                      <tr className={`text-left text-xs font-medium border-b ${isDarkMode ? 'border-gray-800 text-gray-400' : 'border-gray-100 text-gray-500'}`}>
                        <th className="px-4 py-3 w-10" />
                        <th className="px-3 py-3">Client Code</th>
                        <th className="px-3 py-3">Account No</th>
                        <th className="px-3 py-3">Source</th>
                        <th className="px-3 py-3">Clients / Lots</th>
                        <th className="px-3 py-3">Amount</th>
                      </tr>
                    </thead>
                    <tbody className={`divide-y ${isDarkMode ? 'divide-gray-800' : 'divide-gray-50'}`}>
                      {paginatedRows.map((row) => {
                        const expanded = expandedRows.has(row.id);
                        const raw = row.raw || {};
                        return (
                          <React.Fragment key={row.id}>
                            <tr className={isDarkMode ? 'hover:bg-gray-800/50' : 'hover:bg-gray-50/80'}>
                              <td className="px-4 py-3.5">
                                <button
                                  type="button"
                                  onClick={() => toggleRow(row.id)}
                                  className={`inline-flex items-center justify-center w-7 h-7 rounded-lg border transition ${
                                    isDarkMode ? 'border-gray-700 text-gray-400 hover:bg-gray-800' : 'border-gray-200 text-gray-500 hover:bg-gray-50'
                                  }`}
                                  aria-label={expanded ? 'Collapse' : 'Expand'}
                                >
                                  {expanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                                </button>
                              </td>
                              <td className={`px-3 py-3.5 text-sm ${text}`}>{row.clientCode}</td>
                              <td className={`px-3 py-3.5 text-sm ${text}`}>{row.accountNo}</td>
                              <td className={`px-3 py-3.5 text-sm ${text}`}>{row.source}</td>
                              <td className={`px-3 py-3.5 text-sm ${text}`}>{row.lots > 0 ? `${row.lots.toFixed(2)} Lots` : '—'}</td>
                              <td className={`px-3 py-3.5 text-sm font-semibold ${text}`}>{formatCurrency4(row.amount)}</td>
                            </tr>
                            {expanded && (
                              <tr className={isDarkMode ? 'bg-gray-800/30' : 'bg-gray-50/80'}>
                                <td colSpan={6} className="px-5 py-4">
                                  <div className={`rounded-xl border p-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 text-xs ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                                    <div>
                                      <p className={muted}>Symbol</p>
                                      <p className={`font-medium mt-0.5 ${text}`}>
                                        {row.description || raw.symbol || raw.Symbol || '—'}
                                      </p>
                                    </div>
                                    <div>
                                      <p className={muted}>Open time</p>
                                      <p className={`font-medium mt-0.5 ${text}`}>
                                        {formatOpenTime(row.openTime || raw.openTime || raw.startTime)}
                                      </p>
                                    </div>
                                    <div>
                                      <p className={muted}>Close time</p>
                                      <p className={`font-medium mt-0.5 ${text}`}>
                                        {formatOpenTime(row.closeTime || raw.closeTime || raw.endTime)}
                                      </p>
                                    </div>
                                    <div>
                                      <p className={muted}>Open price</p>
                                      <p className={`font-medium mt-0.5 ${text}`}>{formatPrice(raw.openPrice)}</p>
                                    </div>
                                    <div>
                                      <p className={muted}>Close price</p>
                                      <p className={`font-medium mt-0.5 ${text}`}>{formatPrice(raw.closePrice)}</p>
                                    </div>
                                    <div>
                                      <p className={muted}>Trade type</p>
                                      <p className={`font-medium mt-0.5 ${text}`}>{raw.tradeType || '—'}</p>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {!isSubPartnersTab && !(isSubPartnerCommissionsTab ? isSpCommissionsLoading : isListLoading) && totalRecords > 0 && (
                <div className={`px-5 py-4 border-t flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 ${isDarkMode ? 'border-gray-800' : 'border-gray-100'}`}>
                  <div className="flex flex-wrap items-center gap-4">
                    <div className="flex items-center gap-2">
                      <span className={`text-xs ${muted}`}>Show</span>
                      <select
                        value={pageSize}
                        onChange={(e) => setPageSize(e.target.value)}
                        className={`rounded-lg border px-2 py-1.5 text-xs outline-none focus:border-red-800 focus:ring-2 focus:ring-red-800/15 min-w-[3.5rem] ${
                          isDarkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-200 text-gray-900'
                        }`}
                      >
                        {PAGE_SIZE_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                      <span className={`text-xs ${muted}`}>entries</span>
                    </div>
                    <p className={`text-xs ${muted}`}>
                      Showing {showingFrom} to {showingTo} of {totalRecords} entries
                    </p>
                  </div>
                  {totalPages > 1 && (
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      disabled={currentPage <= 1}
                      onClick={() => setCurrentPage((p) => p - 1)}
                      className={`px-3 py-1.5 rounded-lg border text-xs font-medium disabled:opacity-40 disabled:cursor-not-allowed ${
                        isDarkMode ? 'border-gray-700 text-gray-300 hover:bg-gray-800' : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      Prev
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter((page) => page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1)
                      .map((page, i, arr) => (
                        <React.Fragment key={page}>
                          {i > 0 && arr[i - 1] !== page - 1 && (
                            <span className={`px-1 text-xs ${muted}`}>…</span>
                          )}
                          <button
                            type="button"
                            onClick={() => setCurrentPage(page)}
                            className={`min-w-[2rem] px-2 py-1.5 rounded-lg border text-xs font-medium ${
                              currentPage === page
                                ? 'text-white border-transparent'
                                : isDarkMode
                                  ? 'border-gray-700 text-gray-300 hover:bg-gray-800'
                                  : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                            }`}
                            style={currentPage === page ? { backgroundColor: THEME } : {}}
                          >
                            {page}
                          </button>
                        </React.Fragment>
                      ))}
                    <button
                      type="button"
                      disabled={currentPage >= totalPages}
                      onClick={() => setCurrentPage((p) => p + 1)}
                      className={`px-3 py-1.5 rounded-lg border text-xs font-medium disabled:opacity-40 disabled:cursor-not-allowed ${
                        isDarkMode ? 'border-gray-700 text-gray-300 hover:bg-gray-800' : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      Next
                    </button>
                  </div>
                  )}
                </div>
              )}
            </div>
            )}
          </div>

          {isOverview && (
          <div className="space-y-4">
            <div className={`rounded-2xl border p-5 shadow-sm ${card}`}>
              <h3 className={`text-sm font-semibold mb-4 ${text}`}>Earning Summary</h3>
              {isDashboardLoading ? (
                <SectionLoader minHeight="py-8" />
              ) : dashboard && earningSummary?.hasData ? (
                <div className="space-y-3 text-sm">
                  <div className="flex items-center justify-between gap-3">
                    <span className={muted}>Total Clients</span>
                    <span className={`font-semibold ${text}`}>
                      {earningSummary.totalClients.toLocaleString('en-US')}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span className={muted}>Total Lots</span>
                    <span className={`font-semibold ${text}`}>
                      {formatWidgetLots(earningSummary.totalLots)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span className={muted}>Total Profit</span>
                    <span className={`font-semibold ${text}`}>
                      {formatWidgetCurrency(earningSummary.totalProfit)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span className={muted}>Total Records</span>
                    <span className={`font-semibold ${text}`}>
                      {earningSummary.totalRecords.toLocaleString('en-US')}
                    </span>
                  </div>
                </div>
              ) : (
                <p className={`text-sm text-center py-4 ${muted}`}>No summary data available</p>
              )}
            </div>

            <div className={`rounded-2xl border p-5 shadow-sm ${card}`}>
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-xl bg-red-50 flex items-center justify-center shrink-0">
                  <HelpCircle className="w-5 h-5 text-red-800" />
                </div>
                <div>
                  <h3 className={`text-sm font-semibold ${text}`}>Need Help?</h3>
                  <p className={`text-xs mt-1 ${muted}`}>Questions about your commission structure or payouts?</p>
                  <button
                    type="button"
                    onClick={() => window.open(WHATSAPP_URL, '_blank', 'noopener,noreferrer')}
                    className="mt-3 w-full py-2 rounded-xl text-xs font-semibold text-white"
                    style={{ backgroundColor: THEME }}
                  >
                    Contact Support
                  </button>
                </div>
              </div>
            </div>
          </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Commissions;
